import React, { useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FileText, Clock, CreditCard, Eye, CheckCircle2, Loader2 } from "lucide-react";
import KpiCard from "./components/KpiCard";
import QuickAnalyzeCard from "./components/QuickAnalyzeCard";
import RecentAnalysesTable from "./components/RecentAnalysesTable";
import OngoingAnalysesTable from "./components/OngoingAnalysesTable";
import { HistoryResultsModal } from "./HistoryPage";
import PolicyResultModal from "./components/PolicyResultModal";
import TrialBanner from "../../components/ui/TrialBanner";
import Modal from "../../components/ui/Modal";
import TrialPaymentStep from "../auth/components/TrialPaymentStep";
import { useDashboard } from "./hooks/useDashboard";
import { useAuth } from "../auth/hooks/useAuth";
import { adminService } from "../admin/services/adminService";
import { getDomainStats, getPolicyMapReport } from "./services/dashboardService";

// ── Transform /policy-maps/{id}/report → PolicyResultModal shape ────────────
const transformPolicyMapReport = (data) => {
  if (!data) return null;
  const report   = data.report ?? {};
  const privacy  = report.privacy_analysis ?? {};
  const meta     = report.report_metadata  ?? {};
  const categories = Object.entries(privacy).map(([name, d]) => ({
    name,
    score:       d.score            ?? 0,
    maxScore:    10,
    confidence:  d.confidence_score ?? 0,
    verdict:     d.indicator        ?? "Neutral",
    description: d.summary          ?? "",
  }));
  const scores = categories.map((c) => c.score).filter(Boolean);
  const overallScore = scores.length
    ? +(scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(1)
    : (data.overall_score ?? 0);
  const confs = categories.map((c) => c.confidence).filter(Boolean);
  const avgConfidence = confs.length
    ? Math.round(confs.reduce((a, b) => a + b, 0) / confs.length)
    : null;
  return {
    // title:              meta.report_title      ?? `${data.software_name} Privacy Review`,
    reviewedAt:         meta.review_date
                          ? new Date(meta.review_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                          : "",
    policyAsOf:         report.policy_date ?? null,
    overallScore,
    categoriesAnalyzed: categories.length,
    avgConfidence,
    toolSummary:        report.website_summary   ?? "",
    executiveSummary:   report.executive_summary ?? "",
    sourceUrl:          data.privacy_url         ?? "",
    categories,
    softwareName:       data.software_name       ?? "",
  };
};

const DashboardPage = () => {
  const { state: authState } = useAuth();
  useLocation(); // re-render on route state changes
  const rawFirst = (authState?.user?.name || "there").split(" ")[0];
  const firstName = rawFirst.charAt(0).toUpperCase() + rawFirst.slice(1);
  const { kpis, lastActive, recent, recentHasMore, recentLoadingMore, loadMoreRecent, ongoing, loading, subscriptionStatus } = useDashboard();
  const [viewItem,       setViewItem]       = useState(null);
  const [showTrialSetup, setShowTrialSetup] = useState(false);
  const [indivPlan,      setIndivPlan]      = useState(null);

  // ── Changed reports (re-analysed by others) ──────────────────────────────
  const userId = authState?.user?.id || authState?.user?.email;
  const [changedReports,    setChangedReports]    = useState([]);
  const [crModalOpen,       setCrModalOpen]       = useState(false);
  const [crModalResult,     setCrModalResult]     = useState(null);
  const [crModalLoading,    setCrModalLoading]    = useState(false);
  const [crModalError,      setCrModalError]      = useState("");
  const [crModalSoftware,   setCrModalSoftware]   = useState("");
  const [crModalVersions,   setCrModalVersions]   = useState([]);
  const [crModalActiveVer,  setCrModalActiveVer]  = useState("");
  const [domainStatsLoaded, setDomainStatsLoaded] = useState(false);
  const [showCrBanner,      setShowCrBanner]      = useState(false);

  useEffect(() => {
    getDomainStats()
      .then((res) => {
        const reports = res?.data?.my_reports ?? [];
        const within72h = reports.filter((r) => r.has_been_reanalysed_by_anyone && r.is_shown);
        setChangedReports(within72h);
        if (within72h.length > 0) {
          const reportIds = within72h.map((r) => r.global_report_id ?? r.id).sort().join(",");
          const bannerKey = `cr_banner_seen:${userId ?? "guest"}:${reportIds}`;
          if (!localStorage.getItem(bannerKey)) {
            setShowCrBanner(true);
          }
        }
      })
      .catch(() => {})
      .finally(() => setDomainStatsLoaded(true));
  }, [userId]); // eslint-disable-line

  const dismissCrBanner = () => {
    const reportIds = changedReports.map((r) => r.global_report_id ?? r.id).sort().join(",");
    const bannerKey = `cr_banner_seen:${userId ?? "guest"}:${reportIds}`;
    localStorage.setItem(bannerKey, "1");
    setShowCrBanner(false);
  };

  const handleViewChangedReport = useCallback(async (report) => {
    setCrModalResult(null);
    setCrModalError("");
    setCrModalSoftware(report.software_name ?? "");
    const versionNum = report.global_version ?? report.user_version ?? 1;
    const versionKey = `v${versionNum}`;
    const dateStr = report.last_analyzed_at
      ? new Date(report.last_analyzed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
      : "";
    setCrModalVersions([{ key: versionKey, label: `V${versionNum}`, date: dateStr }]);
    setCrModalActiveVer(versionKey);
    setCrModalLoading(true);
    setCrModalOpen(true);
    try {
      const res = await getPolicyMapReport(report.global_report_id);
      setCrModalResult(transformPolicyMapReport(res?.data ?? null));
    } catch (err) {
      setCrModalError(err?.message || "Failed to load report.");
    } finally {
      setCrModalLoading(false);
    }
  }, []);

  const handleView = useCallback((row) => {
    setViewItem(row);
  }, []);

  const trialLsKey = userId ? `trial_setup:${userId}` : null;

  // Show trial payment modal for Individual users who haven't set up a subscription yet
  useEffect(() => {
    // subscriptionStatus === null means still loading
    if (loading || subscriptionStatus === null) return;
    const tier = authState?.user?.plan_tier?.toLowerCase();
    const alreadySetUp = trialLsKey ? !!localStorage.getItem(trialLsKey) : false;
    // Someone who already gave us a card is never asked for one again here.
    const hasCardOnFile = !!subscriptionStatus?.subscription?.id;
    // The card is asked for at the END of the free trial, not at the start:
    // while a trial is running with analyses left, this modal stays closed.
    const trialSpent = !!subscriptionStatus?.usage?.trial_limit_reached;
    // A card is requested ONLY once the free trial is spent — never on arrival.
    // The old condition also fired for "no trial and no subscription", which is
    // the state of a freshly verified user, so the modal greeted people at login.
    const needsCard = !hasCardOnFile && trialSpent;
    // Individual, Professional and Business all run the same card-free trial.
    const trialTier = ['individual', 'professional', 'business'].includes(tier);
    if (trialTier && !alreadySetUp && needsCard) {
      adminService.getPlans()
        .then((res) => {
          const raw = Array.isArray(res)              ? res
                    : Array.isArray(res?.data?.plans) ? res.data.plans
                    : Array.isArray(res?.data)        ? res.data
                    : [];
          const found = raw.find((p) => p.name?.toLowerCase() === 'individual');
          if (found) setIndivPlan({ id: found.id, name: found.name, price: Number(found.price) || 0 });
        })
        .catch(() => {});
      setShowTrialSetup(true);
    }
  }, [loading, subscriptionStatus, authState?.user?.plan_tier, trialLsKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-brand-muted text-sm animate-pulse">Loading dashboard…</p>
      </div>
    );
  }

  if (!kpis) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-brand-muted text-sm">Failed to load dashboard data.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-extrabold text-brand-text">Dashboard</h1>
        <p className="text-sm text-brand-muted mt-0.5 break-words">
          Welcome back, {firstName}. Here's your compliance overview.
        </p>
        <p className="text-xs text-brand-muted mt-1 break-words">
          Last active: {lastActive || "—"}
        </p>
      </div>

      {/* Changed Reports notification banner — shown once per day */}
      {showCrBanner && (
        <div className="rounded-2xl border border-primary/20 bg-primary/5 px-5 py-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-primary">
                {changedReports.length} {changedReports.length === 1 ? "policy has" : "policies have"} been re-analysed
              </p>
              <p className="text-xs text-brand-muted mt-0.5">
                {changedReports.map((r) => r.software_name).join(", ")} —{" "}
                <button
                  onClick={() => {
                    dismissCrBanner();
                    document.getElementById("changed-reports-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="underline text-primary font-medium hover:opacity-80"
                >
                  View updated reports
                </button>
              </p>
            </div>
          </div>
          <button
            onClick={dismissCrBanner}
            className="flex-shrink-0 text-brand-muted hover:text-brand-text text-lg leading-none mt-0.5"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {/* Trial status banner — only visible for Individual plan users */}
      <TrialBanner
        planTier={authState?.user?.plan_tier}
        planStatus={subscriptionStatus?.subscription?.status}
        runsUsed={subscriptionStatus?.usage?.trial_runs_used}
        runLimit={subscriptionStatus?.usage?.trial_run_limit}
        daysRemaining={subscriptionStatus?.usage?.trial_days_remaining}
        limitReached={subscriptionStatus?.usage?.trial_limit_reached}
      />

      {/* KPI row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={FileText}
          iconBg="bg-blue-50"
          iconColor="text-blue-500"
          value={kpis.completed.value}
          label="Completed Analysis"
          sub={kpis.completed.sub}
        />
        <KpiCard
          icon={Clock}
          iconBg="bg-purple-50"
          iconColor="text-purple-500"
          value={kpis.ongoing.value}
          label="Ongoing Analysis"
          sub={kpis.ongoing.sub}
        />
        <KpiCard
          icon={CreditCard}
          iconBg="bg-teal-50"
          iconColor="text-teal-500"
          value={kpis.remaining.value}
          label="Remaining Analysis"
          sub={kpis.remaining.sub}
        />
        <QuickAnalyzeCard />
      </div>

      {/* Tables row */}
      <div className="flex flex-col gap-4">
                <RecentAnalysesTable
            data={recent}
            onView={handleView}
            hasMore={recentHasMore}
            loadingMore={recentLoadingMore}
            onLoadMore={loadMoreRecent}
          />
        <OngoingAnalysesTable data={ongoing} />
      </div>

      {/* Changed Reports table — only shown when there are re-analysed policies */}
      {(changedReports.length > 0 || !domainStatsLoaded) && (
        <div id="changed-reports-section" className="bg-white rounded-2xl border border-surface-border p-4 sm:p-6 flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-success" />
            <h2 className="text-base font-bold text-brand-text">Changed Reports</h2>
            <span className="text-xs font-semibold text-success bg-success/10 px-2 py-0.5 rounded-full">
              {changedReports.length} updated
            </span>
          </div>
          {!domainStatsLoaded ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-4 h-4 text-primary animate-spin" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-border">
                    <th className="text-left text-xs font-medium text-brand-muted pb-3 pr-4">Software</th>
                    <th className="text-left text-xs font-medium text-brand-muted pb-3 pr-4">Privacy URL</th>
                    <th className="text-left text-xs font-medium text-brand-muted pb-3 pr-4">Version</th>
                    <th className="text-left text-xs font-medium text-brand-muted pb-3 pr-4">Last Analysed</th>
                    <th className="text-right text-xs font-medium text-brand-muted pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {changedReports.map((r) => (
                    <tr key={r.id} className="border-b border-surface-border last:border-0">
                      <td className="py-3 pr-4 font-semibold text-brand-text whitespace-nowrap">{r.software_name}</td>
                      <td className="py-3 pr-4 text-brand-muted text-xs break-all max-w-[220px]">{r.privacy_url}</td>
                      <td className="py-3 pr-4 text-brand-muted whitespace-nowrap">v{r.global_version ?? r.user_version}</td>
                      <td className="py-3 pr-4 text-brand-muted whitespace-nowrap text-xs">
                        {r.last_analyzed_at
                          ? new Date(r.last_analyzed_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                          : "—"}
                      </td>
                      <td className="py-3 text-right">
                        <button
                          onClick={() => handleViewChangedReport(r)}
                          className="inline-flex items-center gap-1.5 text-primary text-xs font-semibold hover:underline"
                        >
                          <Eye className="w-3.5 h-3.5" />
                          View Changed Report
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Trial payment setup modal — shown to Individual users with no subscription yet */}
      <Modal
        isOpen={showTrialSetup}
        onClose={() => setShowTrialSetup(false)}
        title="Set up your free trial"
      >
        <TrialPaymentStep
          planId={indivPlan?.id}
          userName={authState?.user?.name}
          userEmail={authState?.user?.email}
          planName={indivPlan?.name ?? 'Individual'}
          planPrice={indivPlan?.price ?? 0}
          onSuccess={() => {
            if (trialLsKey) localStorage.setItem(trialLsKey, '1');
            setShowTrialSetup(false);
            window.location.reload();
          }}
          onBack={() => setShowTrialSetup(false)}
        />
      </Modal>

      {/* Results modal */}
      {viewItem && (
        <HistoryResultsModal
          row={viewItem}
          onClose={() => setViewItem(null)}
        />
      )}

      {/* Changed report modal */}
      {crModalOpen && (
        <PolicyResultModal
          result={crModalResult}
          loading={crModalLoading}
          error={crModalError}
          softwareName={crModalSoftware}
          versions={crModalVersions}
          activeVersion={crModalActiveVer}
          onVersionChange={setCrModalActiveVer}
          onClose={() => { setCrModalOpen(false); setCrModalResult(null); }}
        />
      )}
    </div>
  );
};

export default DashboardPage;
